import React, { type ReactNode, useMemo } from 'react';
import type { Action, ActionImpl, KBarState } from 'kbar';
import {
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarResults,
  KBarSearch,
  useKBar,
  useMatches,
  useRegisterActions
} from 'kbar';
import isString from 'lodash/isString';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import { typeStyle } from 'constants/typography';
import useProjectNavigation from 'hooks/kbar/project-navigation.hooks';
import useModulesSearchActions from 'hooks/kbar/use-modules-actions.hooks';
import useTemplatesSearchActions from 'hooks/kbar/use-templates-kbar-actions.hooks';
import useBasicNavigationActions from 'hooks/kbar/basic-navigation-actions.hooks';
import useOrganizationSettingsTabsActions from 'hooks/kbar/use-settings-tabs-actions.hooks';
import { EventNames, track } from 'utils/analytics.utils';
import type { ActionAnalyticsData } from 'utils/kbar.utils';
import { createActionToDataMap } from 'utils/kbar.utils';
import { useEnvironmentSearchActions } from 'hooks/kbar/use-environment-search-actions.hooks';
import { KbarResultItem } from 'components/kbar/kbar-result-item';
import uniqBy from 'lodash/uniqBy';
import Loader from 'components/common/loader';

let lastVisualState = 'hidden';

const trackKbarState = (state: KBarState) => {
  const visualState = state.visualState;

  if (visualState === 'showing' && lastVisualState !== 'showing') {
    track(EventNames.CMD_K_OPENED);
  }

  lastVisualState = visualState;
  return { searchQuery: state.searchQuery, isKbarOpen: ['showing', 'animating-in'].includes(visualState) };
};

export const KbarWrapper: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const { searchQuery, isKbarOpen } = useKBar(trackKbarState);
  const isSearching = searchQuery.length > 0;
  const basicActions = useBasicNavigationActions();
  const settingsTabsActions = useOrganizationSettingsTabsActions();
  const { actions: projectsActions, isProjectsReady } = useProjectNavigation();
  const { actions: environmentActions, isLoadingSearch } = useEnvironmentSearchActions({
    searchTerm: searchQuery,
    enabled: isKbarOpen && isSearching
  });
  const { actions: templateActions, isLoading: isLoadingTemplates } = useTemplatesSearchActions({
    enabled: isKbarOpen && isSearching && isProjectsReady && !isLoadingSearch
  });
  const { actions: moduleActions, isLoading: isLoadingModules } = useModulesSearchActions({
    enabled: isKbarOpen && isSearching && isProjectsReady && !isLoadingSearch
  });
  const nonProjectSearchActions = useMemo(
    () => [...environmentActions, ...templateActions, ...moduleActions],
    [environmentActions, templateActions, moduleActions]
  );

  const isBlockingSearchResults =
    isSearching && (isLoadingSearch || isLoadingTemplates || isLoadingModules || !isProjectsReady);

  const isLoadingResults = isBlockingSearchResults;

  const allHooksAction = useMemo(() => {
    if (isBlockingSearchResults) return [];

    return uniqBy([...basicActions, ...projectsActions, ...nonProjectSearchActions, ...settingsTabsActions], 'id');
  }, [basicActions, isBlockingSearchResults, nonProjectSearchActions, projectsActions, settingsTabsActions]);

  const actionAnalyticsMap = useMemo(() => createActionToDataMap(allHooksAction), [allHooksAction]);
  const wrappedActions = useMemo(
    () => warpActionsWithAnalytics(allHooksAction, actionAnalyticsMap),
    [allHooksAction, actionAnalyticsMap]
  );
  useRegisterActions(wrappedActions, [wrappedActions, actionAnalyticsMap]);

  return (
    <>
      <KbarPalette isLoading={isLoadingResults} />
      {children}
    </>
  );
};

/**
 * The palette's chrome, without the six action hooks above it - each of those reads the API, and a story
 * must render with no network (frontend/CLAUDE.md). `SearchResults` needs only kbar's own context, so this
 * whole subtree renders under a bare `KBarProvider`. Same DOM, same `data-e2e`.
 */
export const KbarPalette: React.FC<{ isLoading?: boolean }> = ({ isLoading = false }) => (
  <KBarPortal>
    <StyledKbarPositioner>
      <StyledAnimator>
        <StyledSearch data-e2e={`kbar-search`} />
        <SearchResults isLoading={isLoading} />
      </StyledAnimator>
    </StyledKbarPositioner>
  </KBarPortal>
);

const warpActionsWithAnalytics = (actions: Action[], actionToDataMap: Record<string, ActionAnalyticsData>) => {
  return actions.map(action => {
    if (!action.perform) return action;

    const analyticsPerform: Action['perform'] = (currentActionImpl: ActionImpl) => {
      track(EventNames.CMD_K_ITEM_CLICKED, actionToDataMap[action.id]);
      action!.perform!(currentActionImpl);
    };
    return { ...action, perform: analyticsPerform };
  });
};

const SearchResults = ({ isLoading }: { isLoading: boolean }) => {
  const { results, rootActionId } = useMatches();

  if (isLoading) {
    return (
      <LoadingContainer>
        <Loader size={'small'} data-e2e={'kbar-loader'} />
      </LoadingContainer>
    );
  }

  return (
    <>
      <KBarResults
        data-e2e={'kbar-results'}
        items={results}
        onRender={({ item, active }) =>
          isString(item) ? (
            <SectionName data-e2e={`kbar-action-section-${item}`}>{item}</SectionName>
          ) : (
            <KbarResultItem action={item} active={active} currentRootActionId={rootActionId as string} />
          )
        }
      />
    </>
  );
};

/**
 * The palette has no Figma frame of its own (APO-471), so the new design extrapolates from the tokens the
 * antd bridge already gives a card and a form field - `fieldComponent` in `constants/antd.constants`.
 */
const SectionName = styled.div`
  ${legacyOnly(
    theme => css`
      padding: 8px 16px;
      font-size: 10px;
      text-transform: uppercase;
      opacity: 0.5;
      color: ${theme.primaryBlack};
      background: ${theme.kbarSectionBackground};
    `
  )}

  ${newDesignOnly(
    theme => css`
      padding: ${theme.padding.xs} ${theme.padding.lg};
      /* Overline is the ramp's uppercase section label, so it carries the text-transform too. */
      ${typeStyle.overline}
      color: var(--text-secondary);
      background: var(--surface-main-page);
    `
  )}
`;

const StyledSearch = styled(KBarSearch)`
  width: 100%;
  max-width: 600px;
  box-sizing: border-box;
  outline: none;
  border: none;

  ${legacyOnly(
    theme => css`
      padding: 12px 16px;
      font-size: 16px;
      background: ${theme.offWhite};
      color: ${theme.primaryBlack};
      border-radius: 8px;
      overflow: hidden;
    `
  )}

  ${newDesignOnly(
    theme => css`
      padding: ${theme.padding.md} ${theme.padding.lg};
      ${typeStyle.labelMd}
      background: var(--surface-main-cards);
      color: var(--text-heading);
      border-bottom: ${theme.borderWidth.xs} solid var(--border-primary);

      &::placeholder {
        color: var(--text-secondary);
      }
    `
  )}
`;

const StyledAnimator = styled(KBarAnimator)`
  max-width: 600px;
  width: 100%;

  ${legacyOnly(
    theme => css`
      background-color: ${theme.offWhite};
      border-radius: 8px;
    `
  )}

  ${newDesignOnly(
    theme => css`
      background-color: var(--surface-main-cards);
      border: ${theme.borderWidth.xs} solid var(--border-primary);
      border-radius: ${theme.radius['300']};
      box-shadow: var(--shadow-modal);
      overflow: hidden;
    `
  )}
`;

const StyledKbarPositioner = styled(KBarPositioner)`
  background-color: ${({ theme }) => theme.kbarScrim};

  ${legacyOnly(css`
    z-index: 99;
  `)}

  /* At 99 the palette sits under every antd popup, whose base is zIndexPopupBase 1000. */
  ${newDesignOnly(
    theme => css`
      z-index: ${theme.zIndex.modal};
    `
  )}
`;

// 16px is `padding.lg` on both sides, so this needs no branch.
const LoadingContainer = styled.div`
  padding: ${({ theme }) => theme.padding.lg};
  height: 120px;
`;
